import { styled } from '@mui/system'
import {
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Popper,
  Typography,
} from '@mui/material'
import { SettingsOutlined as SettingsIcon } from '@mui/icons-material'

import HarvestExpander from './harvexp'

import { FT_ADMIN_CLAIM, EMPTY_ARRAY } from 'common/c'
import { isNil, get, isEmpty } from 'es-toolkit/compat'

import { getTranslatedName } from 'common/misc'
import colors from 'common/colors'

import LayersGroup from 'components/layers-group/index'

import LayerDownload from 'components/harvest/layer-download/index'

import HarvestSettingsPanel from './harvest-settings'

import { showModal } from 'actions/app'
import { TEST_IDS } from 'test-ids'
import { requestCreateHarvests } from 'api/harvest'

import { getLayerInfoByHarvest } from 'api/field'

import { vectorLayers, rasterLayers } from 'common/layer-data'

//actions

import { harvestFetched } from 'actions/harvest'

import { setLayers } from 'actions/field'
import {
  hideLayerLegend,
  removeLayer,
  showLayer,
  setSwathParameter,
} from 'actions/map'

//hooks
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useState, useCallback, useMemo, useEffect, useRef } from 'react'

//Icons
import { FarmtrxIconWheat2 } from '@farmtrx/farmtrx-icons'

//Types

import { type HarvestModel } from 'state/harvest'

import { type AppState } from 'state/index'

import { type LayerInfoModel } from 'state/field'

export type HarvestPanelProps = {
  anchorEl: null | HTMLElement
  onClickHandler: Function
  open: boolean
  showExportModal: Function
  onClose: Function
  fieldId: number
}

export type HarvestPermissions = {
  editCropCalibration: boolean
  editCropType: boolean
  editSensorCalibration: boolean
  editTimeDelay: boolean
  exportHarvest: boolean
  refreshHarvest: boolean
  setHeader: boolean
  updateTare: boolean
  updateTestWeight: boolean
  updateTotalYield: boolean
  downloadUbj: boolean
  downloadHarvestLog: boolean
  changeHarvestYear: boolean
  createHarvests: boolean
}

export default function HarvestPanel(props: HarvestPanelProps) {
  const { t } = useTranslation()
  const [isHarvestOpen, setIsHarvestOpen] = useState(true)
  const customClaims = useSelector(
    (state: AppState) => state.account.customClaims,
  )
  const permissions = useSelector(
    (state: AppState) => state.account.permissions,
  )
  const dispatch = useDispatch()
  const harvest = useSelector((state: AppState) => state.harvest.current)
  const harvests: HarvestModel[] = useSelector(
    (state: AppState) => state.harvest.collection,
  )
  const [filteredHarvests, setFilteredHarvests] = useState<HarvestModel[]>([])
  const selectedYear = useSelector((state: AppState) => state.field.year)
  const layers = useSelector((state) => get(state, 'field.layers', {}))
  const layerInfo: LayerInfoModel[] = useSelector((state) =>
    get(state, 'field.layerInfo', EMPTY_ARRAY),
  )

  useEffect(() => {
    const _filteredHarvests: HarvestModel[] = []
    for (let i = 0; i < harvests.length; i++) {
      if (harvests[i].fieldId === props.fieldId) {
        _filteredHarvests.push(harvests[i])
      }
    }
    setFilteredHarvests(_filteredHarvests)
  }, [harvests])
  const [showPermissions, setShowPermissions] = useState<HarvestPermissions>({
    editCropCalibration: false,
    editCropType: false,
    editSensorCalibration: false,
    editTimeDelay: false,
    exportHarvest: false,
    refreshHarvest: false,
    setHeader: false,
    updateTare: false,
    updateTestWeight: false,
    updateTotalYield: false,
    downloadUbj: false,
    downloadHarvestLog: false,
    changeHarvestYear: false,
    createHarvests: false,
  })
  const [layerInfoMap, setLayerInfoMap] = useState<{
    [key: string]: LayerInfoModel
  }>({})
  const [harvestLayers, setHarvestLayers] = useState({})
  const [isHarvestActionDrawerOpen, setIsHarvestActionDrawerOpen] =
    useState(false)
  const [harvestOptionsAnchor, setHarvestOptionsAnchor] = useState(null)
  const [createHarvestAnchor, setCreateHarvestAnchor] = useState(null)

  const baseVectorLayers = useMemo(() => {
    const _baseVectorLayers = {}
    for (let i = 0; i < vectorLayers.length; i++) {
      _baseVectorLayers[vectorLayers[i].name] = vectorLayers[i]
    }
    return _baseVectorLayers
  }, [vectorLayers])

  const baseRasterLayers = useMemo(() => {
    const _baseRasterLayers = {}
    for (let i = 0; i < rasterLayers.length; i++) {
      _baseRasterLayers[rasterLayers[i].name] = rasterLayers[i]
    }
    return _baseRasterLayers
  }, [rasterLayers])

  useEffect(() => {
    if (filteredHarvests.length >= 1) {
      setIsHarvestOpen(true)
      dispatch(harvestFetched(filteredHarvests[0]))
    }
  }, [filteredHarvests])

  useEffect(() => {
    setShowPermissions({
      editCropCalibration: permissions.includes('harvest:admin'),
      editCropType: permissions.includes('farm:write'),
      editSensorCalibration: permissions.includes('harvest:admin'),
      editTimeDelay: permissions.includes('harvest:admin'),
      exportHarvest: permissions.includes('farm:read'),
      refreshHarvest: permissions.includes('harvest:admin'),
      setHeader: permissions.includes('harvest:write'),
      updateTare: permissions.includes('harvest:admin'),
      updateTestWeight: permissions.includes('harvest:write'),
      updateTotalYield: permissions.includes('harvest:write'),
      downloadUbj:
        permissions.includes('farmtrx:admin') || customClaims?.[FT_ADMIN_CLAIM],
      downloadHarvestLog:
        permissions.includes('farmtrx:admin') || customClaims?.[FT_ADMIN_CLAIM],
      changeHarvestYear:
        permissions.includes('farmtrx:admin') || customClaims?.[FT_ADMIN_CLAIM],
      createHarvests:
        permissions.includes('farmtrx:admin') || customClaims?.[FT_ADMIN_CLAIM],
    })
  }, [permissions, customClaims])

  useEffect(() => {
    if (!isEmpty(layers) && !isEmpty(harvest)) {
      const id = harvest?.id
      const _harvestLayers =
        id && layers[id] != null
          ? layers[id]
          : Object.assign({}, baseRasterLayers, baseVectorLayers)
      setHarvestLayers(_harvestLayers)
    }
  }, [layers, harvest])

  useEffect(() => {
    if (!isEmpty(layerInfo) && !isEmpty(harvest)) {
      const _layerInfoMap = {}
      layerInfo.map((l) => {
        if (l.harvestId === harvest?.id) {
          _layerInfoMap[l.layerName] = l
        }
      })
      setLayerInfoMap(_layerInfoMap)
    } else {
      setLayerInfoMap({})
    }
  }, [layerInfo, harvest])

  useEffect(() => {
    if (!isNil(harvest)) {
      //@ts-expect-error
      dispatch(getLayerInfoByHarvest(harvest.id, harvest.cropYear))
    }
  }, [harvest])

  useEffect(() => {
    if (!isEmpty(harvest) && layers[harvest.id] == null) {
      const initialLayers = {}
      initialLayers[harvest.id] =
        layers[harvest.id] != null
          ? layers[harvest.id]
          : Object.assign({}, baseRasterLayers, baseVectorLayers)
      dispatch(setLayers(initialLayers))
    }
  }, [harvest, harvestLayers, layers, layerInfo])

  const onHarvestSelected = useCallback(
    (h) => {
      if (h === harvest) {
        setIsHarvestOpen(!isHarvestOpen)
      } else {
        setIsHarvestOpen(true)
        dispatch(harvestFetched(h))

        const initialLayers = {}
        initialLayers[h.id] =
          layers[h.id] != null
            ? layers[h.id]
            : Object.assign({}, baseRasterLayers, baseVectorLayers)
        dispatch(setLayers(initialLayers))
      }

      setIsHarvestActionDrawerOpen(false)
    },
    [layers, harvest, isHarvestOpen],
  )

  const onSettingClick = useCallback((element) => {
    if (element) {
      setIsHarvestActionDrawerOpen(true)
    } else {
      setIsHarvestActionDrawerOpen(false)
    }
    setHarvestOptionsAnchor(element)
  }, [])

  const onCreateHarvestSettingClick = useCallback((element) => {
    setCreateHarvestAnchor(element)
  }, [])

  const onCreateHarvests = useCallback(async () => {
    if (isNil(selectedYear)) {
      return
    }
    await dispatch(requestCreateHarvests(props.fieldId, selectedYear) as any)
    setCreateHarvestAnchor(null)
  }, [dispatch, props.fieldId, selectedYear])

  const harvestLayersRef = useRef(harvestLayers)

  useEffect(() => {
    harvestLayersRef.current = harvestLayers
  }, [harvestLayers])

  const showExportModal = useCallback(
    (h) => {
      dispatch(
        showModal(
          <LayerDownload
            data={{
              id: h.id,
              title: `${h.cropYear}-${getTranslatedName(h.crop.name)}`,
              type: 'harvest',
              tenant_id: h.tenantId,
              startDate: h.startDate,
              endDate: h.endDate,
            }}
            harvestLayers={harvestLayersRef.current}
          />,
        ),
      )
    },
    [harvestLayers, layerInfo],
  )
  return (
    <Popper
      style={{ width: '235px' }}
      anchorEl={props.anchorEl}
      open={props.open}
      placement={'left-start'}
      modifiers={[
        {
          name: 'offset',
          enabled: true,
          options: {
            offset: [140, -240],
            tether: false,
            altBoundary: true,
          },
        },
      ]}
      id={TEST_IDS.harvestPanel}
      data-testid={TEST_IDS.harvestPanel}
    >
      {filteredHarvests.length !== 0 ? (
        <StyledHarvestList>
          {filteredHarvests.map((h) => {
            return (
              <HarvestExpander
                key={h.id}
                id={h.id}
                title={`${h.cropYear}-${getTranslatedName(h.crop.name)}`}
                open={isHarvestOpen && h.id === harvest?.id}
                status={h.status}
                //subscriptionStatus={h?.subscriptionStatus}
                onClick={() => {
                  onHarvestSelected(h)
                }}
                onSettingClick={onSettingClick}
                settingsOpen={isHarvestActionDrawerOpen}
              >
                <HarvestSettingsPanel
                  open={isHarvestActionDrawerOpen}
                  anchorEl={harvestOptionsAnchor}
                  permissions={showPermissions}
                  onClickHandler={props.onClickHandler}
                  harvest={h}
                  showExportModal={showExportModal}
                  onClose={onSettingClick}
                />
                <StyledHarvestListItem key={h.id}>
                  <LayersGroup
                    harvestLayers={harvestLayers}
                    layerInfoMap={layerInfoMap}
                    hideLayerLegend={hideLayerLegend}
                    removeLayer={removeLayer}
                    showLayer={showLayer}
                    harvest={harvest}
                    setLayers={setLayers}
                    setSwathParameter={setSwathParameter}
                  />
                </StyledHarvestListItem>
              </HarvestExpander>
            )
          })}
        </StyledHarvestList>
      ) : (
        <StyledHarvestList>
          <StyledNoHarvestContainer>
            <StyledNoHarvest>
              <FarmtrxIconWheat2 /> {t('no_harvests')}
            </StyledNoHarvest>
            {showPermissions.createHarvests && (
              <>
                <IconButton
                  id={TEST_IDS.harvestSettingsButton}
                  data-testid={TEST_IDS.harvestSettingsButton}
                  onClick={(e) => onCreateHarvestSettingClick(e.currentTarget)}
                  size="small"
                  aria-label={t('create_harvests')}
                  aria-controls={
                    createHarvestAnchor ? TEST_IDS.harvestSettings : undefined
                  }
                  aria-haspopup="true"
                  aria-expanded={createHarvestAnchor ? 'true' : undefined}
                >
                  <SettingsIcon
                    titleAccess={t('create_harvests')}
                    style={{ height: '0.7em' }}
                  />
                </IconButton>
                <Menu
                  anchorEl={createHarvestAnchor}
                  open={Boolean(createHarvestAnchor)}
                  anchorOrigin={{ vertical: -12, horizontal: 50 }}
                  id={TEST_IDS.harvestSettings}
                  data-testid={TEST_IDS.harvestSettings}
                  slotProps={{
                    list: { 'aria-labelledby': TEST_IDS.harvestSettingsButton },
                  }}
                  onClose={() => onCreateHarvestSettingClick(null)}
                  disableEnforceFocus={true}
                  closeAfterTransition={false}
                >
                  <MenuList style={{ outline: 0 }}>
                    <MenuItem
                      id={TEST_IDS.createHarvests}
                      data-testid={TEST_IDS.createHarvests}
                      disabled={isNil(selectedYear)}
                      onClick={onCreateHarvests}
                    >
                      <FarmtrxIconWheat2
                        style={{ margin: 4, height: 16, width: 16 }}
                      />
                      <Typography sx={{ fontSize: '.75rem' }}>
                        {t('create_harvests').toUpperCase()}
                      </Typography>
                    </MenuItem>
                  </MenuList>
                </Menu>
              </>
            )}
          </StyledNoHarvestContainer>
        </StyledHarvestList>
      )}
    </Popper>
  )
}

const StyledHarvestList = styled('ul')({
  paddingLeft: 0,
  marginBottom: 0,
  borderRadius: '0.25rem',
  padding: '1px 0',
  background: colors.white,
})

const StyledHarvestListItem = styled('li')({
  position: 'relative',
  display: 'block',
  padding: '10px 5px',
  textDecoration: 'none',
  backgroundColor: colors.white,
  '&:first-of-type': {
    borderTopLeftRadius: 'inherit',
    borderTopRightRadius: 'inherit',
  },
  '& + &': {
    borderTopWidth: 0,
  },
  '&:last-child': {
    borderBottomRightRadius: 'inherit',
    borderBottomLeftRadius: 'inherit',
  },
})

const StyledNoHarvestContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingRight: '8px',
})

const StyledNoHarvest = styled('span')({
  paddingLeft: '8px',
  fontSize: '11px',
  fontWeight: 500,
  color: '#000000',
  opacity: 0.4,
  '& svg': {
    fontSize: '16px',
    '& path': {
      stroke: '#000000',
    },
  },
})
